import { supabase } from '../supabaseClient';
import {
  buildSealedTagRaw,
  collectSealedTagTokens,
  isSealedTagBody,
  MEMO_TAG_SEALED_PREFIX,
  normalizeTagBodyToMemberOrNull,
} from '../utils/classRecordMemoHashtags';
import type { ClassMemberRow } from '../utils/classMembersStorage';
import {
  getSealedTagLabelMap,
  peekSealedTagLabel,
  rememberSealedTagLabel,
} from '../utils/memoTagLabelCache';

export { getSealedTagLabelMap, rememberSealedTagLabel, peekSealedTagLabel } from '../utils/memoTagLabelCache';

/**
 * 미등록 태그 본문을 Vault로 봉인해 `#e:…` raw 토큰을 반환한다.
 * (이미 `#e:` / `e:` 이면 그대로)
 */
export async function sealMemoTagBody(typedBody: string): Promise<string> {
  const body = (typedBody ?? '').trim();
  if (!body) throw new Error('empty tag body');
  if (isSealedTagBody(body)) return `#${body}`;
  if (body.startsWith('#') && isSealedTagBody(body.slice(1))) return body;

  let toSeal = body;
  if (toSeal.endsWith('-') && toSeal.length > 1 && !toSeal.startsWith(MEMO_TAG_SEALED_PREFIX)) {
    toSeal = toSeal.slice(0, -1);
  }

  const { data, error } = await supabase.rpc('memo_tag_seal', { p_body: toSeal });
  if (error) throw new Error(error.message);
  const sealedRaw = typeof data === 'string' ? data.trim() : '';
  if (!sealedRaw.startsWith('#e:')) throw new Error('invalid seal response');
  rememberSealedTagLabel(sealedRaw, toSeal);
  return sealedRaw;
}

/**
 * 입력 태그 본문 → 저장용 raw(`#m:…` / `#전체` / `#e:…`).
 */
export async function resolveStorageTagRaw(
  typedBody: string,
  members: ClassMemberRow[]
): Promise<string> {
  const body = (typedBody ?? '').trim();
  if (!body) return '';
  const normalized = normalizeTagBodyToMemberOrNull(body, members);
  if (normalized === '전체') return '#전체';
  if (normalized) return `#${normalized}`;
  return sealMemoTagBody(body);
}

/**
 * 여러 텍스트에서 `#e:` 토큰을 모아 1회 배치 복호화. 결과는 세션 맵에 병합.
 * 토큰이 없으면 RPC를 호출하지 않는다.
 */
export async function ensureSealedTagsUnsealed(texts: string[]): Promise<Record<string, string>> {
  const need: string[] = [];
  const seen = new Set<string>();
  for (const text of texts) {
    for (const token of collectSealedTagTokens(text)) {
      if (peekSealedTagLabel(token) || seen.has(token)) continue;
      seen.add(token);
      need.push(token);
    }
  }
  if (need.length === 0) return getSealedTagLabelMap();

  const { data, error } = await supabase.rpc('memo_tag_unseal_batch', { p_tokens: need });
  if (error) throw new Error(error.message);
  const map = (data ?? {}) as Record<string, string>;
  Object.entries(map).forEach(([token, label]) => {
    if (typeof token === 'string' && typeof label === 'string' && label) {
      rememberSealedTagLabel(token.startsWith('#') ? token : buildSealedTagRaw(token.replace(/^e:/, '')), label);
      rememberSealedTagLabel(token, label);
    }
  });
  return getSealedTagLabelMap();
}
